import { useState, useEffect, useCallback } from "react";
import {
  getSystemConfig,
  bulkUpdateSystemConfig,
  resetConfigGroup,
} from "../../../api/apiServices";

// ── Shared Toggle ─────────────────────────────────────────────
function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${checked ? "bg-[#00C896]" : "bg-white/10"}`}
    >
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? "left-5" : "left-0.5"}`} />
    </button>
  );
}

// ── Config field row ──────────────────────────────────────────
function ConfigRow({ setting, onChange, saving }) {
  const isBoolean = setting.type === "boolean" || typeof setting.value === "boolean";
  const isPercent = setting.key.includes("percent") || setting.key.includes("cashback") || setting.key.includes("rate");

  return (
    <div className="flex items-center justify-between py-3.5 border-b border-white/5 last:border-0 gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-white truncate">{setting.label}</p>
        <p className="text-xs text-white/35 mt-0.5 leading-relaxed">{setting.description}</p>
      </div>
      <div className="flex-shrink-0">
        {isBoolean ? (
          <Toggle
            checked={Boolean(setting.value)}
            onChange={() => onChange(setting.key, !setting.value)}
            disabled={saving}
          />
        ) : (
          <div className="relative">
            <input
              type="number"
              step={isPercent ? "0.001" : "1"}
              value={setting.value}
              onChange={e => onChange(setting.key, setting.type === "number" ? parseFloat(e.target.value) : e.target.value)}
              disabled={saving}
              className="w-28 bg-[#0f0f0f] border border-white/8 rounded-xl px-3 py-1.5 text-sm text-white text-right focus:outline-none focus:border-[#00C896]/50 disabled:opacity-40"
            />
            {isPercent && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 text-xs pointer-events-none">
                {setting.value > 1 ? "%" : "×"}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Group card ────────────────────────────────────────────────
function ConfigGroup({ title, icon, color, settings, onChange, saving }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/2 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: color + "20" }}>
            <i className={`${icon} text-sm`} style={{ color }} />
          </div>
          <span className="text-sm font-semibold text-white capitalize">{title}</span>
          <span className="text-xs bg-white/5 text-white/30 px-2 py-0.5 rounded-full">{settings.length}</span>
        </div>
        <i className={`ri-arrow-${open ? "up" : "down"}-s-line text-white/30 text-sm`} />
      </button>
      {open && (
        <div className="px-5 pb-2 border-t border-white/5">
          {settings.map(s => (
            <ConfigRow key={s.key} setting={s} onChange={onChange} saving={saving} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Existing static data ──────────────────────────────────────
const teamMembers = [
  { id: 1, name: "Super Admin",   email: "admin@cryptong.io",  role: "Super Admin",      lastLogin: "Now",       status: "active" },
  { id: 2, name: "Chidi Okafor",  email: "chidi@cryptong.io",  role: "KYC Officer",      lastLogin: "1 hr ago",  status: "active" },
  { id: 3, name: "Amaka Eze",     email: "amaka@cryptong.io",  role: "Support Agent",    lastLogin: "3 hr ago",  status: "active" },
  { id: 4, name: "Tunde Bello",   email: "tunde@cryptong.io",  role: "Finance Officer",  lastLogin: "Yesterday", status: "inactive" },
];

// Labels for config keys (makes raw DB keys human-readable)
const CONFIG_LABELS = {
  "reward.milestone.first_kyc":             "KYC Completion Bonus (₦)",
  "reward.milestone.first_buy":             "First Crypto Buy Bonus (₦)",
  "reward.milestone.first_sell":            "First Crypto Sell Bonus (₦)",
  "reward.milestone.first_bill_payment":    "First Bill Payment Bonus (₦)",
  "reward.milestone.first_crypto_deposit":  "First Crypto Deposit Bonus (₦)",
  "reward.milestone.first_fiat_deposit":    "First Naira Deposit Bonus (₦)",
  "reward.milestone.first_bank_link":       "First Bank Link Bonus (₦)",
  "reward.streak.3day":                     "3-Day Streak Reward (₦)",
  "reward.streak.7day":                     "7-Day Streak Reward (₦)",
  "reward.streak.30day":                    "30-Day Streak Reward (₦)",
  "reward.cashback.bill_payment":           "Bill Payment Cashback Rate",
  "reward.cashback.buy":                    "Crypto Buy Cashback Rate",
  "reward.cashback.sell":                   "Crypto Sell Cashback Rate",
  "reward.cashback.max_per_tx":             "Max Cashback Per Transaction (₦)",
  "reward.referral.on_signup":              "Referral Signup Reward (₦)",
  "reward.referral.on_first_trade":         "Referral First Trade Reward (₦)",
  "reward.min_withdrawal":                  "Min Reward Withdrawal (₦)",
  "trading.fee.buy_percent":                "Crypto Buy Fee (%)",
  "trading.fee.sell_percent":               "Crypto Sell Fee (%)",
  "autoCreditBuy":                          "Auto-Credit Crypto After Buy",
  "deposit.fee.fiat_percent":               "Naira Deposit Fee (%)",
  "deposit.min_fiat":                       "Minimum Naira Deposit (₦)",
  "features.rewards_enabled":              "Enable Reward System",
  "features.referral_enabled":             "Enable Referral Rewards",
  "features.cashback_enabled":             "Enable Cashback Rewards",
};

const GROUP_META = {
  rewards:  { icon: "ri-gift-line",        color: "#00C896" },
  trading:  { icon: "ri-exchange-line",    color: "#60a5fa" },
  deposits: { icon: "ri-bank-card-line",   color: "#f59e0b" },
  features: { icon: "ri-toggle-line",      color: "#a78bfa" },
  other:    { icon: "ri-settings-3-line",  color: "#94a3b8" },
};

// ─────────────────────────────────────────────────────────────
export default function SettingsView() {
  const [savedKey, setSavedKey] = useState(null);


  const [notifs, setNotifs] = useState({
    kycAlerts: true, largeWithdrawals: true, failedTxns: true,
    newUsers: false, priceAlerts: true, systemAlerts: true,
  });

  // ── System config (from API) ──────────────────────────────
  const [configGroups, setConfigGroups]   = useState({});   // { group: [setting, ...] }
  const [pendingChanges, setPendingChanges] = useState({}); // { key: newValue }
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving]   = useState(false);
  const [configError, setConfigError]     = useState(null);

  // Fetch all config on mount
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const data = await getSystemConfig();
      if (data.status) {
        // Enrich each setting with human-readable label
        const enriched = {};
        Object.entries(data.data).forEach(([group, settings]) => {
          enriched[group] = settings.map(s => ({
            ...s,
            label: CONFIG_LABELS[s.key] || s.key,
          }));
        });
        setConfigGroups(enriched);
      }
    } catch (err) {
      setConfigError("Failed to load configuration. Check your connection.");
      console.error("[CONFIG]", err);
    } finally {
      setConfigLoading(false);
    }
  };

  // Track changes locally before saving
  const handleConfigChange = useCallback((key, value) => {
    setPendingChanges(prev => ({ ...prev, [key]: value }));
    // Also update the display immediately
    setConfigGroups(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(group => {
        updated[group] = updated[group].map(s =>
          s.key === key ? { ...s, value } : s
        );
      });
      return updated;
    });
  }, []);

  // Save all pending changes in one bulk request
  const handleSaveConfig = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setConfigSaving(true);
    try {
      const settings = Object.entries(pendingChanges).map(([key, value]) => ({ key, value }));
      await bulkUpdateSystemConfig(settings);
      setPendingChanges({});
      setSavedKey("config");
      setTimeout(() => setSavedKey(null), 2000);
    } catch (err) {
      alert("Failed to save configuration. Please try again.");
      console.error("[CONFIG SAVE]", err);
    } finally {
      setConfigSaving(false);
    }
  };

  // Reset a group to defaults
  const handleResetGroup = async (group) => {
    if (!window.confirm(`Reset all "${group}" settings to default values? Admin changes will be lost.`)) return;
    try {
      await resetConfigGroup(group);
      await fetchConfig();
      setSavedKey(`reset_${group}`);
      setTimeout(() => setSavedKey(null), 2000);
    } catch (err) {
      alert("Reset failed.");
    }
  };

  const handleSave = (key) => {
    setSavedKey(key);
    setTimeout(() => setSavedKey(null), 2000);
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-6">

      {/* Content */}
      <div className="flex-1 space-y-4 min-w-0">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">System Configuration</p>
                <p className="text-xs text-white/35 mt-0.5">Live values used by the backend — changes apply within 60 seconds</p>
              </div>
              <div className="flex items-center gap-2">
                {hasPendingChanges && (
                  <span className="text-xs text-amber-400 bg-amber-400/10 px-3 py-1.5 rounded-lg font-medium">
                    {Object.keys(pendingChanges).length} unsaved change{Object.keys(pendingChanges).length > 1 ? "s" : ""}
                  </span>
                )}
                <button onClick={fetchConfig} disabled={configLoading}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors disabled:opacity-40">
                  <i className={`ri-refresh-line text-sm ${configLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>

            {configError && (
              <div className="flex items-center gap-3 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <i className="ri-error-warning-line text-red-400" />
                <p className="text-sm text-red-300">{configError}</p>
                <button onClick={fetchConfig} className="ml-auto text-xs text-red-400 underline">Retry</button>
              </div>
            )}

            {configLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-16 bg-[#161616] border border-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {Object.entries(configGroups).map(([group, settings]) => {
                  const meta = GROUP_META[group] || GROUP_META.other;
                  return (
                    <div key={group}>
                      <ConfigGroup
                        title={group}
                        icon={meta.icon}
                        color={meta.color}
                        settings={settings}
                        onChange={handleConfigChange}
                        saving={configSaving}
                      />
                      <div className="flex justify-end mt-1.5">
                        <button onClick={() => handleResetGroup(group)}
                          className="text-xs text-white/25 hover:text-white/50 transition-colors px-2 py-1">
                          {savedKey === `reset_${group}` ? "✓ Reset done" : `Reset "${group}" to defaults`}
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Sticky save bar — shows when there are pending changes */}
                {hasPendingChanges && (
                  <div className="sticky bottom-4 z-10 flex items-center justify-between px-5 py-3.5 bg-[#1a1a1a] border border-[#00C896]/30 rounded-2xl shadow-2xl">
                    <div className="flex items-center gap-2.5">
                      <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                      <p className="text-sm text-white/70">
                        <span className="text-white font-semibold">{Object.keys(pendingChanges).length}</span> unsaved{" "}
                        {Object.keys(pendingChanges).length === 1 ? "change" : "changes"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setPendingChanges({}); fetchConfig(); }}
                        className="px-4 py-2 rounded-xl text-xs text-white/40 hover:text-white/70 transition-colors">
                        Discard
                      </button>
                      <button onClick={handleSaveConfig} disabled={configSaving}
                        className="px-5 py-2 rounded-xl bg-[#00C896] text-black text-xs font-bold hover:bg-[#00b386] transition-colors disabled:opacity-50 flex items-center gap-2">
                        {configSaving
                          ? <><div className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" />Saving…</>
                          : "Save All Changes"}
                      </button>
                    </div>
                  </div>
                )}

                {savedKey === "config" && !hasPendingChanges && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#00C896]/10 border border-[#00C896]/20 rounded-xl">
                    <i className="ri-checkbox-circle-line text-[#00C896]" />
                    <p className="text-sm text-[#00C896] font-medium">All changes saved. Backend cache will refresh within 60 seconds.</p>
                  </div>
                )}
              </>
            )}
          </div>
      </div>
    </div>
  );
}
